import { useMemo } from 'react';
import { LoadingSection } from '@/components/ui';

// Per-stage labels + a short descriptive sub-hint. The sub-hint intentionally
// describes WHAT is happening rather than promising a time — real durations vary
// with video length, device load, and queue wait during concurrent usage, so a
// "usually N seconds" estimate would routinely be wrong (and stressful) when the
// job is waiting behind others. See backlog/frontend-loading-ux-long-operations.md.
//
// Stage strings emitted by the backend (evaluation_pipeline.py + evaluation_worker.py):
//   pose_extraction       — ViTPose body detection (10-30% progress)
//   movement_preparation  — phase loading, side/camera checks, checkpoint
//                           generation, body-orientation estimate (45%)
//   scoring               — form scoring against the template (75% progress)
//   coaching              — LLM narrative generation (95% progress)
//   completed             — pipeline finished
//   person_N              — multi-person: scoring person index N
//   person_N_done         — multi-person: person N complete
// Unknown/future stages fall back to a generic "Processing…" label.

// What the 45% step does, in the coach's words: it decides which side leads,
// checks the camera, finds the phases and builds the athlete's checkpoints, so
// the two performances can be compared. Nothing for the coach to do — this is a
// progress label, so the WHY lives in the sub-hint and the HOW is "wait".
const MOVEMENT_PREPARATION_HINT = {
  label: 'Preparing the movement for comparison…',
  hint: 'Lining your movement up with the demo, phase by phase',
};

const STAGE_HINTS: Record<string, { label: string; hint: string }> = {
  pose_extraction: {
    label: 'Analyzing movement…',
    hint: 'Tracking your body through each frame',
  },
  movement_preparation: MOVEMENT_PREPARATION_HINT,
  // DEPRECATED ALIAS — "3d_lifting" was this stage's id until 2026-10-07, when
  // it was renamed to say what the step actually does (the 3D lift it was named
  // for was deleted by the 3D retirement, so the old label claiming a 3D model
  // was being built had become untrue). A job that was already queued or
  // processing when the rename deployed carries the old id on its DB row, so it
  // must keep resolving to the SAME label (shared object, so the two cannot
  // drift) for one release. Drop this entry once no pre-rename job row can
  // still be polled (eval job rows are purged after
  // SPORTFORM_EVAL_JOB_RETENTION_DAYS, default 90d).
  '3d_lifting': MOVEMENT_PREPARATION_HINT,
  scoring: {
    label: 'Scoring against the template…',
    hint: 'Comparing your form to the reference',
  },
  // coaching and llm_coaching are both emitted by different code paths
  coaching: {
    label: 'Writing your coaching feedback…',
    hint: 'Crafting personalized tips for you',
  },
  llm_coaching: {
    label: 'Writing your coaching feedback…',
    hint: 'Crafting personalized tips for you',
  },
  saving: {
    label: 'Saving results…',
    hint: 'Almost there',
  },
  completed: {
    label: 'Done!',
    hint: '',
  },
};

/**
 * Translate a raw progress_stage string into a user-friendly label.
 * Handles the standard pipeline stages and multi-person "person_N" / "person_N_done" variants.
 * Returns null when progressStage is null/undefined (no stage yet — fallback to card title).
 */
function resolveStageLabel(progressStage: string | null | undefined): { label: string; hint: string | undefined } | null {
  if (!progressStage) return null;
  // Multi-person stages: "person_0", "person_1", "person_2_done", etc.
  const multiPersonMatch = progressStage.match(/^person_(\d+)(_done)?$/);
  if (multiPersonMatch) {
    const personNum = parseInt(multiPersonMatch[1], 10) + 1; // 0-indexed → display as 1-indexed
    const isDone = Boolean(multiPersonMatch[2]);
    return {
      label: isDone ? `Scored athlete ${personNum}` : `Scoring athlete ${personNum}…`,
      hint: undefined,
    };
  }
  const entry = STAGE_HINTS[progressStage];
  if (entry) return { label: entry.label, hint: entry.hint || undefined };
  // Unknown/future stage — generic fallback
  return { label: 'Processing…', hint: undefined };
}

interface EvaluationProgressCardProps {
  /** Current stage identifier from the job poll response (e.g. "pose_extraction"). */
  progressStage?: string | null;
  /** 0-100 progress value from the job poll response. Omit for indeterminate. */
  progressPct?: number | null;
  /** When the job was submitted (for elapsed counter). */
  startedAt?: Date;
  className?: string;
}

/**
 * EvaluationProgressCard — displays a labelled progress bar for the current
 * evaluation pipeline stage, with a "usually X-Y seconds" hint and an elapsed
 * counter via LoadingSection.
 *
 * Consumes `progress_pct` and `progress_stage` from GET /api/evaluations/jobs/{job_id}.
 */
function EvaluationProgressCard({
  progressStage,
  progressPct,
  startedAt,
  className,
}: EvaluationProgressCardProps) {
  const stageInfo = useMemo(() => resolveStageLabel(progressStage), [progressStage]);

  return (
    <LoadingSection
      title="Evaluating performance…"
      stage={stageInfo?.label}
      pct={progressPct ?? undefined}
      startedAt={startedAt}
      expectedDurationLabel={stageInfo?.hint}
      className={className}
    />
  );
}

export default EvaluationProgressCard;
